import React, { useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  IconButton,
  DialogContent,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { useNavigate } from "react-router-dom";
import { useCalculateEmi } from "../../../hooks/use-emicalculation";
import { useEmiAdminConfig } from "../../../hooks/useEmiAdminConfig";
import { formatAmount } from "../../utils/formatAmount";
import FormTextField from "../FormTextField";
import FormSelectField from "../FormTextField";
import { useCreateDriverLead } from "../../../hooks/use-driver";
import FormSelect from "../FormSelect";
import DocumentChecklist from "../../preOnboarding/documentChecklist/DocumentChecklist";
import { getErrorMessage } from "../../../utils/getErrorMessage";
import paths from "../../../routes/paths";

const safeAmount = (value) => formatAmount(Number(value) || 0);

const safeInterestRate = (rate) =>
  typeof rate === "number" && !Number.isNaN(rate)
    ? `${(rate * 100).toFixed(0)}%`
    : "--";

const safeEmiValue = (emi, key) => {
  const value = emi?.[key];
  return value != null && !Number.isNaN(Number(value))
    ? safeAmount(value)
    : "--";
};

const getDownpaymentOptions = (emiAdminConfig) => {
  if (!emiAdminConfig?.downPayment) return [];
  const { min, max, step } = emiAdminConfig.downPayment;
  const options = [];
  for (let i = min; i <= max; i += step) {
    options.push(i);
  }
  return options;
};

const getFinancierKey = (option, index) =>
  option?.financierInternal || option?.financier || `financier-${index}`;

const EMI_CADENCES = [
  { key: "monthly", label: "Monthly" },
  { key: "weekly", label: "Weekly" },
  { key: "daily", label: "Daily" },
];

const EmiCalculationStep = ({ data, onChange, allFormData, leadId }) => {
  const navigate = useNavigate();

  const {
    data: emiAdminConfig,
    isLoading: isConfigLoading,
    error: configError,
  } = useEmiAdminConfig("v2");

  const {
    mutate: calculateEmi,
    isPending: isCalculating,
    isError: isCalcError,
    error: calcError,
  } = useCalculateEmi("v2");

  const { mutate: saveLead, isPending: isSaving } = useCreateDriverLead();

  const [results, setResults] = useState(null);

  const [checklistOpen, setChecklistOpen] = useState(false);
  const [selectedFinancierKey, setSelectedFinancierKey] = useState(null);

  const [selectedFinancier, setSelectedFinancier] = useState(null);

  const driverInformation = allFormData?.driverInformation || {};
  const cibilCheck = allFormData?.cibilCheck || {};

  const tenureOptions = Array.isArray(emiAdminConfig?.tenures)
    ? emiAdminConfig.tenures
    : [];

  const tenureSelectOptions = tenureOptions.map((tenure) => ({
    label: `${tenure} months`,
    value: tenure,
  }));

  const downpaymentOptions = getDownpaymentOptions(emiAdminConfig);

  const downpaymentSelectOptions = downpaymentOptions.map((dp) => ({
    label: safeAmount(dp),
    value: dp,
  }));

  const isCalculateDisabled =
    !data?.invoiceAmount ||
    !data?.tenure ||
    !data?.downPayment ||
    isConfigLoading ||
    Boolean(configError);

  const handleCalculate = () => {
    const invoiceAmount = Number(data?.invoiceAmount) || 0;
    const tenure = Number(data?.tenure) || 0;
    const downPayment = Number(data?.downPayment) || 0;
    const cibilScore = parseInt(cibilCheck?.cibilNumber, 10) || 0;

    if (!invoiceAmount || !tenure || !downPayment) return;

    calculateEmi(
      {
        cibilScore,
        downPayment,
        tenure,
        invoiceAmount,
        mode: "all",
      },
      {
        onSuccess: (response) => {
          if (!response || typeof response !== "object") {
            setResults([]);
            return;
          }

          const { mode, ...financierResults } = response;
          const parsedResults = Object.values(financierResults || {}).filter(
            (item) => item && typeof item === "object",
          );

          setResults(parsedResults);
        },
      },
    );
  };

  const handleContinue = (option, index) => {
    if (!leadId) return;

    const financierKey = getFinancierKey(option, index);
    setSelectedFinancierKey(financierKey);

    saveLead(
      {
        leadId,
        loanSummary: option,
      },
      {
        onSuccess: () => {
          navigate(`${paths.DRIVER_ONBOARDING}?tab=leads`);
        },
        onSettled: () => {
          setSelectedFinancierKey(null);
        },
      },
    );
  };

  const handleSelectFinancier = (f) => {
    setSelectedFinancier(f);
    setChecklistOpen(true);
  };

  const handleCloseChecklist = () => {
    setChecklistOpen(false);
  };

  return (
    <Box>
      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={4}>
          <FormTextField
            label="Invoice Value"
            required
            type="number"
            value={data?.invoiceAmount || ""}
            onChange={(value) => onChange("invoiceAmount", value)}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <FormSelect
            label="Select Tenure"
            required
            value={data?.tenure || ""}
            onChange={(value) => onChange("tenure", value)}
            options={tenureSelectOptions}
            disabled={isConfigLoading || Boolean(configError)}
            placeholder="Select tenure"
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <FormSelect
            label="Select Down Payment"
            required
            value={data?.downPayment || ""}
            onChange={(value) => onChange("downPayment", value)}
            options={downpaymentSelectOptions}
            disabled={isConfigLoading || Boolean(configError)}
            placeholder="Select down payment"
          />
        </Grid>
      </Grid>

      {configError && (
        <Typography sx={{ fontSize: 13, color: "#B3261E", mb: 2 }}>
          Unable to load EMI configuration. Please refresh and try again.
        </Typography>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          my: 3,
        }}
      >
        <Button
          variant="contained"
          onClick={handleCalculate}
          disabled={isCalculateDisabled || isCalculating}
          startIcon={
            isCalculating ? (
              <CircularProgress size={16} sx={{ color: "#fff" }} />
            ) : null
          }
          sx={{
            borderRadius: 2,
            backgroundColor: "#0E7A6E",
            fontWeight: 600,
            textTransform: "none",
            boxShadow: "none",
            "&:hover": { backgroundColor: "#0B5F56", boxShadow: "none" },
          }}
        >
          {isCalculating ? "Calculating..." : "Calculate EMI"}
        </Button>
      </Box>

      {isCalcError && (
        <Typography sx={{ fontSize: 13, color: "#B3261E", mb: 2 }}>
          {getErrorMessage(calcError, "Failed to calculate EMI")}
        </Typography>
      )}

      {results && results.length === 0 && (
        <Typography sx={{ fontSize: 13, color: "#66766F", mb: 2 }}>
          No financier options were returned for these inputs.
        </Typography>
      )}

      {results && results.length > 0 && (
        <Grid container spacing={2}>
          {results.map((option, index) => {
            const financierKey = getFinancierKey(option, index);
            const isThisSaving =
              isSaving && selectedFinancierKey === financierKey;

            return (
              <Grid item xs={12} sm={6} md={4} key={financierKey}>
                <Card
                  sx={{
                    borderRadius: 3,
                    border: "1px solid #E6ECEA",
                    boxShadow: "none",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <CardContent
                    sx={{ flex: 1, display: "flex", flexDirection: "column" }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: 15,
                        mb: 1.5,
                        color: "#12181A",
                      }}
                    >
                      {option?.financier || "Unknown financier"}
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 0.75,
                      }}
                    >
                      <Typography sx={{ fontSize: 12.5, color: "#66766F" }}>
                        Loan Amount
                      </Typography>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>
                        {option?.loanAmount != null
                          ? safeAmount(option.loanAmount)
                          : "--"}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 0.75,
                      }}
                    >
                      <Typography sx={{ fontSize: 12.5, color: "#66766F" }}>
                        Interest Rate
                      </Typography>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>
                        {safeInterestRate(option?.interestRate)}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1.5,
                      }}
                    >
                      <Typography sx={{ fontSize: 12.5, color: "#66766F" }}>
                        Tenure
                      </Typography>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>
                        {option?.tenure != null
                          ? `${option.tenure} months`
                          : "--"}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        backgroundColor: "#E6F5F2",
                        borderRadius: 2,
                        p: 1.5,
                        mb: 2,
                        display: "flex",
                      }}
                    >
                      {EMI_CADENCES.map((cadence, cadenceIndex) => (
                        <Box
                          key={cadence.key}
                          sx={{
                            flex: 1,
                            textAlign: "center",
                            borderLeft:
                              cadenceIndex === 0
                                ? "none"
                                : "1px solid rgba(11,95,86,0.15)",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 10.5,
                              color: "#0B5F56",
                              fontWeight: 650,
                              textTransform: "uppercase",
                              letterSpacing: "0.02em",
                            }}
                          >
                            {cadence.label}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: cadence.key === "monthly" ? 17 : 14,
                              fontWeight: 700,
                              color: "#0B5F56",
                              mt: 0.25,
                            }}
                          >
                            {safeEmiValue(option?.emi, cadence.key)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    <Button
                      fullWidth
                      variant="contained"
                      disabled={isSaving || !leadId}
                      onClick={() => handleContinue(option, index)}
                      startIcon={
                        isThisSaving ? (
                          <CircularProgress size={16} sx={{ color: "#fff" }} />
                        ) : null
                      }
                      sx={{
                        mt: "auto",
                        borderRadius: 2,
                        backgroundColor: "#0E7A6E",
                        fontWeight: 600,
                        textTransform: "none",
                        boxShadow: "none",
                        "&:hover": {
                          backgroundColor: "#0B5F56",
                          boxShadow: "none",
                        },
                      }}
                    >
                      {isThisSaving ? "Saving..." : "Continue"}
                    </Button>
                    <Button
                      disabled={isSaving}
                      variant="outlined"
                      sx={{ mt: 1 }}
                      onClick={() => handleSelectFinancier(option?.financier)}
                    >
                      Doc Checklist
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Dialog
        open={checklistOpen}
        onClose={handleCloseChecklist}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
          },
        }}
      >
        <DialogContent
          dividers
          sx={{
            p: 0,
            px: 3,
          }}
        >
          <DocumentChecklist
            financierType={selectedFinancier}
            isActionButton={false}
            actions={[<Button onClick={handleCloseChecklist}>Close</Button>]}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default EmiCalculationStep;
